import { FileUploader } from "devextreme-react/ui/file-uploader";
import React, { useCallback, useMemo, useRef } from "react";
import { CommentType } from "../../../../../dtos/CommentType";
import { GrantDto } from "../../../../../dtos/GrantDto";
import { StepDto } from "../../../../../dtos/StepDto";
import { useTaskFileUploadController } from "../hooks/useTaskFileUploadController";
import "./FileUploader.css";
import { FileUploaderView } from "./FileUploaderView";
import { countStepFiles, stepFileLimit, uploadExtensions } from "../../stepFiles";
import { useTaskListContext, useTaskListSelectionState } from "../../context/TaskListContext";
import { useAppContext } from "../../../AppContext";

// With streaming off, chunks collect in server memory and the original size stays. On, each
// chunk goes straight on to SharePoint as a 10 MB piece, so chunks are that size too.
export const CHUNK_SIZE = 200_000;
export const STREAMING_CHUNK_SIZE = 10 * 1024 * 1024;

// The server accepts 2 GB, but with streaming off the whole file is held in server memory and sent
// to SharePoint in one request, which SharePoint refuses above ~250 MB. On, it goes through
// SharePoint's upload session, so the server limit is the only one left.
export const MAX_FILE_SIZE = 50_000_000;
export const STREAMING_MAX_FILE_SIZE = 2_000_000_000;

type Props = {
    grant: GrantDto;
    step: StepDto;
    xsrfToken: string;
    isDisabled: boolean;
    fileType?: CommentType;
    // Submission slot uploads: route to a named slot with that slot's allowed extensions.
    fileLabel?: string;
    allowedExtensions?: string[];
    // Fired after a successful upload (e.g. to close a hosting dialog).
    onUploadComplete?: () => void;
    // Pick or drop several files in one go. Off for slot uploads - a slot holds one file.
    multiple?: boolean;
    // Runs after a valid file is selected and BEFORE the upload starts (awaited). Used by the
    // slot Replace flow to delete the existing file first (the backend rejects a duplicate name).
    // When set, the uploader switches to manual mode and triggers the upload once this resolves.
    beforeUpload?: () => Promise<void>;
};

export default function TaskFileUploader({
    grant,
    step,
    xsrfToken,
    isDisabled,
    fileType,
    fileLabel,
    allowedExtensions,
    onUploadComplete,
    beforeUpload,
    multiple,
}: Props) {
    const fileUploader = useRef<FileUploader>(null);
    const { selectedPhase } = useTaskListSelectionState();
    const { tasklistMode } = useTaskListContext();
    const app = useAppContext();
    const streaming = app.configuration?.streamingFileTransfer === true;
    const chunkSize = streaming ? STREAMING_CHUNK_SIZE : CHUNK_SIZE;
    const maxFileSize = streaming ? STREAMING_MAX_FILE_SIZE : MAX_FILE_SIZE;

    // A slot names its own types; everywhere else the subtask's own list applies, and a subtask
    // without one takes the types its task gives new subtasks - the same fallback to
    // DefaultStepSettings that permissions and notifications use (ticket 1067). The seeders keep
    // that task-level list current, so there is no list in the frontend to fall back to.
    const configuredExtensions = step.defaultStep?.settings?.allowedExtensions
        ?? selectedPhase?.defaultPhase?.settings?.defaultStepSettings?.allowedExtensions;
    const effectiveExtensions = useMemo(
        () => allowedExtensions ?? uploadExtensions(configuredExtensions) ?? undefined,
        [allowedExtensions, configuredExtensions]
    );

    const {
        uploadUrl,
        accept,
        allowedFileExtensions,
        onBeforeSend,
        onUploadProgress,
        onUploadStarted,
        onUploaded,
        onUploadError,
        onUploadAborted,
        onValueChange,
    } = useTaskFileUploadController({ xsrfToken, stepId: step.id, fileType, fileLabel, allowedExtensions: effectiveExtensions, onUploaded: onUploadComplete });

    const beforeUploadRef = useRef(beforeUpload);
    beforeUploadRef.current = beforeUpload;

    const handleValueChange = useCallback(async (files: File[]) => {
        const valid = onValueChange(files, () => fileUploader.current?.instance.reset());
        if (!valid || !beforeUploadRef.current) return;
        // Manual-mode replace: remove the existing file, then start the upload deterministically.
        try {
            await beforeUploadRef.current();
        } catch {
            fileUploader.current?.instance.reset();
            return;
        }
        fileUploader.current?.instance.upload();
    }, [onValueChange]);

    // Disable uploader if step doesn't have an ID yet (unsaved subtask)
    const effectiveIsDisabled = isDisabled || !step.id;

    // The server refuses an upload once the subtask holds its limit, whichever area the files
    // went to. Say so here instead of offering a picker whose every upload would fail. A document
    // slot is exempt: its Replace deletes the old file before the new one goes up.
    const fileLimit = tasklistMode !== "templates" && !fileLabel ? stepFileLimit(step.defaultStep?.settings) : null;
    const fileCount = countStepFiles(step);
    if (!effectiveIsDisabled && fileLimit != null && fileCount >= fileLimit) {
        return (
            <div className="file-limit-reached" data-testid="file-limit-reached">
                File limit reached ({fileCount} of {fileLimit}). Delete a file to upload another.
            </div>
        );
    }

    return (
        <FileUploaderView
            fileUploaderRef={fileUploader}
            uploadUrl={uploadUrl}
            isDisabled={effectiveIsDisabled}
            // Replace flow controls upload timing manually (delete-then-upload); others upload instantly.
            uploadMode={beforeUpload ? "useButtons" : "instantly"}
            multiple={multiple}
            chunkSize={chunkSize}
            maxFileSize={maxFileSize}
            accept={accept}
            allowedFileExtensions={allowedFileExtensions}
            onBeforeSend={onBeforeSend}
            onUploadStarted={onUploadStarted}
            onProgress={onUploadProgress}
            onUploaded={onUploaded}
            onUploadError={onUploadError}
            onUploadAborted={onUploadAborted}
            onValueChange={handleValueChange}
        />
    );
}
